import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaGoogle, FaFacebookF, FaApple } from 'react-icons/fa';
import { Parse } from 'parse';
import { useAuth } from '../../context/AuthContext';
import appConfig from '../../config/appConfig';
import { useTranslation } from 'react-i18next';

// Import TokLive logo
import tokLiveLogo from '../../assets/icons/ic_logo_dark.png';

const LoginContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a2a6c, #b21f1f, #fdbb2d);
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: white;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: white;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  
  @media (max-width: 768px) {
    border-radius: 0;
    flex: 1;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 2rem;
  color: #1a2a6c;
  
  img {
    height: 80px;
    margin-right: 15px;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
  color: #666;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const StyledField = styled(Field)`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    border-color: #1a2a6c;
    outline: none;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 1rem;
  color: #999;
`;

const PasswordToggle = styled.div`
  position: absolute;
  right: 1rem;
  top: 1rem;
  color: #999;
  cursor: pointer;
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  margin-left: 0.5rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background-color: #1a2a6c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #152354;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ForgotPassword = styled(Link)`
  display: block;
  text-align: right;
  margin-bottom: 1.5rem;
  color: #1a2a6c;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BackToHome = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 2rem;
  color: #666;
  text-decoration: none;
  
  &:hover {
    color: #1a2a6c;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  
  &:before, &:after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #ddd;
  }
  
  span {
    padding: 0 1rem;
    color: #888;
    font-size: 0.875rem;
  }
`;

const SocialLoginButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SocialButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  background-color: white;
  color: #333;
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f5f5f5;
    border-color: #ccc;
  }
  
  &.google:hover {
    color: #DB4437;
  }
  
  &.facebook:hover {
    color: #4267B2;
  }
  
  &.apple:hover {
    color: #000;
  }
`;

const FeatureList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-top: 2rem;
`;

const FeatureItem = styled.li`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  
  &:before {
    content: '✓';
    margin-right: 0.5rem;
    color: #fdbb2d;
    font-weight: bold;
  }
`;

const LoginSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login, setCurrentUser, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // If user is already authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      // Use the login function from AuthContext instead of directly calling authService
      await login(values.username, values.password);
      toast.success(t('auth.login.success'));
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(t('auth.login.error'));
      setFieldError('password', t('auth.login.invalidCredentials'));
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSocialLogin = async (provider) => {
    try {
      let user;
      switch (provider) {
        case 'google':
          // Ensure Google Sign-In API is loaded
          if (!window.gapi) {
            throw new Error('Google Sign-In API not loaded');
          }

          // Initialize Google Sign-In
          const auth2 = await new Promise((resolve, reject) => {
            window.gapi.load('auth2', () => {
              window.gapi.auth2.init({
                client_id: appConfig.socialLogin?.google?.clientId,
                scope: 'profile email'
              }).then(resolve).catch(reject);
            });
          });

          // Trigger Google Sign-In
          const googleUser = await auth2.signIn();
          const idToken = googleUser.getAuthResponse().id_token;

          // Authenticate with Parse Server using Google ID token
          user = await Parse.User.logInWith('google', { 
            id_token: idToken,
          });
          break;

        case 'facebook':
          // Ensure Facebook SDK is loaded
          if (!window.FB) {
            throw new Error('Facebook SDK not loaded');
          }

          // Trigger Facebook Login
          const { authResponse } = await new Promise((resolve, reject) => {
            window.FB.login((response) => {
              if (response.authResponse) {
                resolve(response);
              } else {
                reject(new Error('Facebook login cancelled'));
              }
            }, { scope: 'public_profile,email' });
          });

          // Authenticate with Parse Server using Facebook access token
          user = await Parse.User.logInWith('facebook', {
            access_token: authResponse.accessToken,
          });
          break;

        case 'apple':
          // Ensure Apple Sign-In is loaded
          if (!window.AppleID) {
            throw new Error('Apple Sign-In not loaded');
          }

          // Initialize Apple Sign-In
          const appleConfig = appConfig.socialLogin?.apple;
          if (!appleConfig?.clientId) {
            throw new Error('Apple Client ID not configured');
          }

          // Trigger Apple Sign-In
          const appleResponse = await window.AppleID.auth.signIn();

          // Authenticate with Parse Server using Apple identity token
          user = await Parse.User.logInWith('apple', {
            id_token: appleResponse.authorization.id_token,
            // Include user info if provided (only sent on first login)
            user: appleResponse.user ? {
              name: appleResponse.user.name,
              email: appleResponse.user.email
            } : undefined
          });
          break;

        default:
          throw new Error('Unsupported social provider');
      }

      // Update current user in auth context
      setCurrentUser(user);
      toast.success(t('auth.login.success'));
      navigate('/dashboard');
    } catch (error) {
      console.error(`${provider} login error:`, error);
      toast.error(t('auth.login.socialLoginFailed', { provider }));
    }
  };

  return (
    <LoginContainer>
      <LeftPanel>
        <h1 style={{ fontSize: '3rem', marginBottom: '2rem' }}>{t('app.name')}</h1>
        <p style={{ fontSize: '1.5rem', marginBottom: '2rem', maxWidth: '500px', textAlign: 'center' }}>
          {t('app.tagline')}
        </p>
        
        <FeatureList>
          <FeatureItem>{t('auth.welcome.features.hostManagement')}</FeatureItem>
          <FeatureItem>{t('auth.welcome.features.analytics')}</FeatureItem>
          <FeatureItem>{t('auth.welcome.features.payments')}</FeatureItem>
          <FeatureItem>{t('auth.welcome.features.scheduling')}</FeatureItem>
          <FeatureItem>{t('auth.welcome.features.security')}</FeatureItem>
        </FeatureList>
      </LeftPanel>
      
      <RightPanel>
        <Logo>
          <img src={tokLiveLogo} alt="TokLive Logo" />
        </Logo>
        <FormContainer>
          <Title>{t('auth.login.title')}</Title>
          <Subtitle>{t('auth.login.subtitle')}</Subtitle>
          
          <Formik
            initialValues={{ username: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <SocialLoginButtons>
                  <SocialButton 
                    type="button" 
                    className="google" 
                    onClick={() => handleSocialLogin('google')}
                    aria-label="Sign in with Google"
                  >
                    <FaGoogle />
                  </SocialButton>
                  <SocialButton 
                    type="button" 
                    className="facebook" 
                    onClick={() => handleSocialLogin('facebook')}
                    aria-label="Sign in with Facebook"
                  >
                    <FaFacebookF />
                  </SocialButton>
                  <SocialButton 
                    type="button" 
                    className="apple" 
                    onClick={() => handleSocialLogin('apple')}
                    aria-label="Sign in with Apple"
                  >
                    <FaApple />
                  </SocialButton>
                </SocialLoginButtons>
                
                <OrDivider>
                  <span>{t('auth.login.or')}</span>
                </OrDivider>
                
                <FormGroup>
                  <InputIcon>
                    <FaUser />
                  </InputIcon>
                  <StyledField
                    type="text"
                    name="username"
                    placeholder={t('auth.login.username')}
                  />
                  <ErrorMessage name="username" component={ErrorText} />
                </FormGroup>
                
                <FormGroup>
                  <InputIcon>
                    <FaLock />
                  </InputIcon>
                  <StyledField
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder={t('auth.login.password')}
                  />
                  <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                  <ErrorMessage name="password" component={ErrorText} />
                </FormGroup>
                
                <ForgotPassword to="/forgot-password">
                  {t('auth.login.forgotPassword')}
                </ForgotPassword>
                
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('auth.login.loggingIn') : t('auth.login.loginButton')}
                </Button>
              </Form>
            )}
          </Formik>
          
          <BackToHome to="/">
            ← {t('auth.login.backToHome')}
          </BackToHome>
        </FormContainer>
      </RightPanel>
    </LoginContainer>
  );
};

export default LoginPage;
